import {Button, Card, CardActions, CardContent, colors, Modal, Theme, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import React, {useState} from 'react';
import {T} from 'react-polyglot-hooks';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {FilterValues, initialFilters} from '../ResourceList/ResourcesSearchFilter/ResourcesSearchFilter';
import {OPERATOR, WhereConstraints} from '../../../graphql/common.datatypes';
import getResources from './getResources.api';
import anyLoading from '../../../utils/anyLoading';
import SearchBar from '../../../components/SearchBar';
import Results from './Results';
import updateResource from '../ResourceEdit/updateResource.api';
import {useSnackbar} from 'notistack';
import {Resource} from '../../../graphql/resources.datatypes';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '90%',
    overflowY: 'auto',
    maxWidth: '95%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  deleteButton: {
    color: theme.palette.white,
    backgroundColor: colors.red[600],
    '&:hover': {
      backgroundColor: colors.red[900]
    }
  },
  submitButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  results: {
    marginTop: theme.spacing(3)
  }
}));

const ChangeResourceParentModal = (props: {
  className?: string,
  onClose: () => void,
  onUpdate?: () => void,
  open: boolean,
  resource: Resource,
}) => {
  const {open, onClose, onUpdate, resource, ...rest} = props;
  const classes = useStyles();
  const [sendUpdateResource, updateData] = updateResource();
  const {enqueueSnackbar} = useSnackbar();

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [orderBy] = useState([{column: 'id', order: 'ASC'}]);

  const getResourcesWhereConstraints = (newFilters: FilterValues) :WhereConstraints => {
    const constraints: WhereConstraints = {AND: [], OR: []};

    if(newFilters.search !== '') {
      const block :WhereConstraints = {OR: []};
      block.OR!.push({column: 'display_name', operator: OPERATOR.LIKE, value: `%${newFilters.search}%`});
      block.OR!.push({column: 'internal_name', operator: OPERATOR.LIKE, value: `%${newFilters.search}%`});
      block.OR!.push({column: 't_and_c_url', operator: OPERATOR.LIKE, value: `%${newFilters.search}%`});
      constraints.AND!.push(block);
    }

    constraints.AND!.push({column: 'id', operator: OPERATOR.NEQ, value: resource.id});
    constraints.AND!.push({column: 'disabled', operator: OPERATOR.EQ, value: newFilters.disabled});

    return constraints;
  };

  const [filters, setFilters] = useState<FilterValues>({...initialFilters()});
  const [whereResources, setWhereResources] = useState<WhereConstraints>(getResourcesWhereConstraints(filters));
  const resourcesData = getResources(rowsPerPage, page, whereResources, orderBy);
  const loadingData = anyLoading(resourcesData);
  const resources = resourcesData && resourcesData.data && resourcesData.data.resources;

  const handleSearch = (value: string) => {
    const newFilters = {...initialFilters(), search: value};
    setFilters(newFilters);
    setWhereResources(getResourcesWhereConstraints(newFilters));
  };

  const handleChangeParent = (newParentId: number) => {
    sendUpdateResource({variables: {data: {id: resource.id, parent_resource_id: newParentId}}})
        .then(() => {
          onClose();
          enqueueSnackbar('Resource Parent Updated', {variant: 'success'});
        })
        .catch(() => {
          onClose();
          enqueueSnackbar('Resource Update Failed!', {variant: 'error'});
        });
  };

  if (!open) {
    return null;
  }

  return (
      <Modal onClose={onClose} open={open}>
        <Card {...rest} className={clsx(classes.root)}>
          <form>
            <LoadingOverlay loading={loadingData.loading}>
              <CardContent>
                <Typography gutterBottom variant="h3">Change Parent: {resource.internal_name} </Typography>

                <Typography variant="body1" color={'error'}>
                  Warning! You are about to change the parent for a resource. Please be sure about this update as it
                  could cause problems and cannot be undone.<br/>
                  This should only be done on new resources, or newly cloned resources. If you change the parent of an
                  exisitng resource with options that have been applied to rooms and members it will result in errors in
                  billing party calculations.
                </Typography>

                <SearchBar onSearch={handleSearch} />

                {resources && (
                    <Results
                        resources={resources.data}
                         className={classes.results}
                         paginatorInfo={resources.paginatorInfo}
                         setPage={setPage}
                         rowsPerPage={rowsPerPage}
                         setRowsPerPage={setRowsPerPage}
                        onRowClick={handleChangeParent}
                    />
                )}

              </CardContent>
              <CardActions className={classes.actions}>
                <Button onClick={onClose} variant="contained"><T phrase="Cancel"/></Button>
              </CardActions>
            </LoadingOverlay>
          </form>
        </Card>
      </Modal>
  );
};

export default ChangeResourceParentModal;
